import { incrementTelemetry } from 'core-events/actions';
import noop from 'lodash/noop';
import { JSX, useContext } from 'react';
import { SearchFilterContentTypes } from 'search-gateway-client/components';
import { twMerge } from 'tailwind-merge';

import { type ContentClassEnum } from '@videoblocks/content-service-client';

import { NavSearchContext } from '../../context/NavSearchContext';
import { ContentType } from '../../types/shared';
import {
    contentTypeToSearchFilterContentType,
    RelatedContentTypeLabel,
    WithinContentClassLabel,
} from '../NavSearchElements/NavSearchTypes';
import { suggestionTypesKey } from './constants';

export type WithinAssetContentTypeSuggestion = {
    keyword: string;
    contentType: ContentType;
};

export type WithinAssetSuggestions = {
    contentClass: string;
    withinAssetContentTypeSuggestions: WithinAssetContentTypeSuggestion[];
};

export interface WithinAssetSuggestionProps {
    titleClassName?: string;
    contentClass?: string;
    withinAssetContentTypeSuggestions?: WithinAssetContentTypeSuggestion[];
    onSelect?: (value: string, contentType?: SearchFilterContentTypes) => void;
    selectedIndex?: number;
    useNewSearchUI?: boolean;
    useWarpSearchUI?: boolean;
}

const WithinAssetSuggestion = ({
    titleClassName = '',
    contentClass = '',
    withinAssetContentTypeSuggestions = [],
    onSelect = noop,
    selectedIndex = -1,
    useNewSearchUI,
    useWarpSearchUI = false,
}: WithinAssetSuggestionProps): JSX.Element | false => {
    const context = useContext(NavSearchContext);
    const commonClassName =
        'related-collection-title text-sm border-gray-500 border-0 border-b lg:min-w-[9.5rem] min-w-[21vw] border-solid leading-5 font-normal';

    return (
        !!(contentClass && withinAssetContentTypeSuggestions?.length) && (
            <div className="flex flex-col gap-y-2.5">
                <div
                    className={twMerge(
                        useNewSearchUI
                            ? 'related-collection-title text-xs border-gray-300 border-0 border-b w-full lg:min-w-[9.5rem] min-w-[21vw] border-solid leading-5 font-normal'
                            : useWarpSearchUI
                              ? `${commonClassName} w-full`
                              : `${commonClassName} w-3/4`,
                        titleClassName,
                    )}
                >
                    {`Within ${WithinContentClassLabel[contentClass as ContentClassEnum]}`}
                </div>
                <ul className={'flex flex-col min-w-[316px]'}>
                    {withinAssetContentTypeSuggestions.map((suggestion, index) => {
                        return (
                            <li
                                role="button"
                                tabIndex={0}
                                id={`${suggestionTypesKey.WITHIN_ASSET}${index}`}
                                key={index}
                                className={twMerge(
                                    'flex cursor-pointer text-sm leading-4 list-none whitespace-nowrap py-2 items-center gap-x-2 hover:bg-gray-100',
                                    useNewSearchUI && '-ml-2 pl-2 rounded-md',
                                    index === selectedIndex && useNewSearchUI && 'bg-gray-100',
                                    index === selectedIndex &&
                                        !useNewSearchUI &&
                                        'bg-gray-100 border-solid border-2 border-blue-500',
                                )}
                                onClick={() => {
                                    void incrementTelemetry('typeahead.withinContentClass.click');
                                    onSelect(
                                        suggestion.keyword,
                                        contentTypeToSearchFilterContentType[suggestion.contentType],
                                    );
                                }}
                                onKeyDown={() => {
                                    void incrementTelemetry('typeahead.withinContentClass.click');
                                    onSelect(
                                        suggestion.keyword,
                                        contentTypeToSearchFilterContentType[suggestion.contentType],
                                    );
                                }}
                            >
                                <svg
                                    width="8"
                                    height="10"
                                    viewBox="0 0 8 10"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5.16481 9.77344L7.90348 7.46021L5.16481 5.14699V6.88634H0L0 8.03408H5.16481V9.77344Z"
                                        fill="black"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.11136e-05 -1.04904e-05L3.11136e-05 7.46027H1.14777L1.14777 -1.04904e-05H3.11136e-05Z"
                                        fill="black"
                                    />
                                </svg>
                                <div className="font-normal">
                                    <span className="font-bold">{suggestion.keyword}</span>{' '}
                                    {`in ${RelatedContentTypeLabel[suggestion.contentType]}`}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        )
    );
};

export default WithinAssetSuggestion;
